class YunaInfiniteCollection {
  constructor(container) {
    this.container = container;
    this.grid = null;
    this.pagination = null;
    this.loader = null;
    this.status = null;
    this.button = null;
    this.nextUrl = null;
    this.loading = false;
    this.observer = null;
    this.gridObserver = null;
    this.mount();
    this.watchForFacetUpdates();
  }

  mount() {
    const nextGrid = this.container.querySelector('[data-yuna-product-grid]');
    if (!nextGrid || nextGrid === this.grid) return;

    this.disconnect();
    this.grid = nextGrid;
    this.pagination = this.container.querySelector('[data-yuna-pagination]');
    this.loader = this.container.querySelector('[data-yuna-infinite-loader]');
    this.status = this.container.querySelector('[data-yuna-infinite-status]');
    this.button = this.container.querySelector('[data-yuna-infinite-button]');
    this.nextUrl = this.getNextUrl();
    this.loading = false;

    if (this.button) {
      this.button.addEventListener('click', () => this.loadNextPage());
    }

    if (!this.nextUrl) {
      this.showEndState();
      return;
    }

    this.loader.hidden = false;
    this.button.hidden = true;
    this.status.textContent = '';

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '700px 0px' }
      );
      this.observer.observe(this.loader);
    } else {
      this.button.hidden = false;
    }
  }

  watchForFacetUpdates() {
    this.gridObserver = new MutationObserver(() => this.mount());
    this.gridObserver.observe(this.container, { childList: true, subtree: true });
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    this.observer = null;
  }

  getNextUrl() {
    const nextLink = this.pagination?.querySelector('a.pagination__item--prev');
    return nextLink?.href || null;
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.loader.hidden = false;
    this.loader.classList.add('is-loading');
    this.button.hidden = true;
    this.status.textContent = 'Loading more designs';

    try {
      const response = await fetch(this.nextUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = documentFragment.querySelector('[data-yuna-product-grid]');
      const incomingPagination = documentFragment.querySelector('[data-yuna-pagination]');

      if (!incomingGrid) throw new Error('The next collection page did not include a product grid.');

      const incomingItems = [...incomingGrid.children];
      incomingItems.forEach((item) => {
        item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
        item.removeAttribute('data-cascade');
        item.style.removeProperty('--animation-order');
        this.grid.appendChild(item);
      });

      if (this.pagination) {
        this.pagination.innerHTML = incomingPagination?.innerHTML || '';
      }

      this.nextUrl = this.getNextUrl();
      this.status.textContent = '';
      history.replaceState(
        { ...history.state, yunaLoadedProducts: this.grid.children.length },
        document.title
      );

      if (this.nextUrl) {
        this.observer?.observe(this.loader);
      } else {
        this.showEndState();
      }
    } catch (error) {
      this.status.textContent = 'Could not load more designs. Try again.';
      this.button.textContent = 'Load more designs';
      this.button.hidden = false;
    } finally {
      this.loading = false;
      this.loader.classList.remove('is-loading');
    }
  }

  showEndState() {
    this.disconnect();
    if (!this.loader || !this.status || !this.button) return;
    this.loader.hidden = false;
    this.loader.classList.remove('is-loading');
    this.status.textContent = 'You have seen every design.';
    this.button.hidden = true;
  }
}

class YunaBottomStickyFilters {
  constructor() {
    this.toolbar = null;
    this.placeholder = null;
    this.threshold = 0;
    this.frame = null;
    this.observer = new MutationObserver(() => this.bind());
    this.update = this.update.bind(this);

    this.bind();
    this.observer.observe(document.getElementById('MainContent') || document.body, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);
  }

  bind() {
    const nextToolbar = document.querySelector('.yn-collection-shell .facets-wrapper');
    if (!nextToolbar || nextToolbar === this.toolbar) return;

    this.toolbar?.classList.remove('is-bottom-stuck');
    this.placeholder?.remove();
    this.toolbar = nextToolbar;
    this.placeholder = document.createElement('div');
    this.placeholder.className = 'yn-collection-filter-placeholder';
    this.placeholder.setAttribute('aria-hidden', 'true');
    this.toolbar.before(this.placeholder);
    this.threshold = this.toolbar.getBoundingClientRect().top + window.scrollY;
    this.update();
  }

  update() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      if (!this.toolbar?.isConnected || !this.placeholder?.isConnected) {
        this.bind();
        return;
      }

      if (!this.toolbar.classList.contains('is-bottom-stuck')) {
        this.threshold = this.toolbar.getBoundingClientRect().top + window.scrollY;
      }

      const shouldStick = window.scrollY > this.threshold;
      this.toolbar.classList.toggle('is-bottom-stuck', shouldStick);
      this.placeholder.style.height = shouldStick ? `${this.toolbar.offsetHeight}px` : '0px';
      this.frame = null;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('ProductGridContainer');
  if (container) new YunaInfiniteCollection(container);
  if (document.querySelector('.yn-collection-shell .facets-wrapper')) new YunaBottomStickyFilters();
});

document.addEventListener('click', (event) => {
  const colourFilter = event.target.closest('[data-yuna-colour-filter]');
  if (!colourFilter) return;

  event.preventDefault();
  const destination = new URL(colourFilter.href, window.location.origin);
  const current = new URL(window.location.href);

  current.searchParams.forEach((value, key) => {
    if (key !== 'page' && key !== 'section_id' && !destination.searchParams.has(key)) {
      destination.searchParams.append(key, value);
    }
  });

  window.location.assign(destination.toString());
});
